import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, } from "@mui/material";
/** How long (ms) between each keepalive prompt while the dashboard is open. */
const DEFAULT_PROMPT_AFTER_MS = 10 * 60 * 1000; // 10 minutes
/** How long (ms) the user has to respond before being logged out. */
const DEFAULT_TIMEOUT_AFTER_MS = 3 * 60 * 1000; // 3 minutes
/**
 * Shows a periodic "Are you still there?" dialog while a Superset iframe
 * dashboard is open, to keep the server-side session alive.
 *
 * Because the dashboard lives inside a cross-origin iframe we cannot detect
 * real user interactions with it, so we use this timed prompt instead.
 *
 * Every `promptAfterMs` (default 10 min) the dialog appears. The user then has
 * `timeoutAfterMs` (default 3 min) to click "Yes, I'm still here", which fires
 * `onKeepalive`. If they don't respond in time, `onTimeout` is called instead.
 */
export function useSupersetKeepalive({ onKeepalive, onTimeout, promptAfterMs = DEFAULT_PROMPT_AFTER_MS, timeoutAfterMs = DEFAULT_TIMEOUT_AFTER_MS, }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [remainingMs, setRemainingMs] = useState(timeoutAfterMs);
    const [isKeepingAlive, setIsKeepingAlive] = useState(false);
    /**
     * Incrementing this counter resets the 10-minute prompt timer.
     * We increment it after the user confirms they are still here.
     */
    const [promptCycle, setPromptCycle] = useState(0);
    // Stable refs so that interval/timeout callbacks always read the latest
    // values without needing to be included in dependency arrays.
    const onKeepaliveRef = useRef(onKeepalive);
    const onTimeoutRef = useRef(onTimeout);
    useEffect(() => {
        onKeepaliveRef.current = onKeepalive;
    }, [onKeepalive]);
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);
    // ── Main prompt timer ──────────────────────────────────────────────────────
    // Resets whenever the user confirms presence (promptCycle increments).
    useEffect(() => {
        const promptTimer = setTimeout(() => {
            setRemainingMs(timeoutAfterMs);
            setDialogOpen(true);
        }, promptAfterMs);
        return () => {
            clearTimeout(promptTimer);
        };
        // promptCycle intentionally included to restart the timer after each confirmation.
    }, [promptAfterMs, timeoutAfterMs, promptCycle]);
    // ── Countdown ticker (runs only while the dialog is open) ─────────────────
    useEffect(() => {
        if (!dialogOpen)
            return;
        const TICK_MS = 1000;
        // Track remaining time in a local variable so the setInterval callback
        // stays pure — side-effects (setDialogOpen, onTimeout) are called directly
        // in the interval callback rather than inside a state updater, which
        // prevents double-invocations under React Strict Mode.
        let remaining = timeoutAfterMs;
        const ticker = setInterval(() => {
            remaining -= TICK_MS;
            if (remaining <= 0) {
                clearInterval(ticker);
                setDialogOpen(false);
                setRemainingMs(0);
                onTimeoutRef.current();
            }
            else {
                setRemainingMs(remaining);
            }
        }, TICK_MS);
        return () => {
            clearInterval(ticker);
        };
    }, [dialogOpen, timeoutAfterMs]);
    // ── User confirmation handler ──────────────────────────────────────────────
    const handleStillHere = useCallback(async () => {
        setIsKeepingAlive(true);
        try {
            await onKeepaliveRef.current();
            setDialogOpen(false);
            // Incrementing promptCycle restarts the 10-minute prompt timer via the
            // useEffect above, which is cleaned up correctly on unmount.
            setPromptCycle((c) => c + 1);
        }
        catch {
            // If the keepalive API call fails the session may already be invalid, so
            // treat it as a timeout rather than silently restarting the prompt cycle.
            setDialogOpen(false);
            onTimeoutRef.current();
        }
        finally {
            setIsKeepingAlive(false);
        }
    }, []);
    // ── Dialog UI ─────────────────────────────────────────────────────────────
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const progressValue = timeoutAfterMs > 0 ? Math.max(0, (remainingMs / timeoutAfterMs) * 100) : 0;
    const keepaliveDialog = (_jsxs(Dialog, { open: dialogOpen, "aria-labelledby": "superset-keepalive-dialog-title", "aria-describedby": "superset-keepalive-dialog-description", disableEscapeKeyDown: true, children: [_jsx(DialogTitle, { id: "superset-keepalive-dialog-title", children: "Are you still there?" }), _jsxs(DialogContent, { children: [_jsxs(DialogContentText, { id: "superset-keepalive-dialog-description", children: ["You have been viewing this dashboard for a while. To keep your session active, please confirm you are still here. You will be logged out automatically in ", remainingSeconds, " second", remainingSeconds !== 1 ? "s" : "", "."] }), _jsx(LinearProgress, { variant: "determinate", value: progressValue, sx: { mt: 2 }, "aria-label": "Time remaining before automatic logout" })] }), _jsx(DialogActions, { children: _jsx(Button, { onClick: () => void handleStillHere(), variant: "contained", disabled: isKeepingAlive, startIcon: isKeepingAlive ? _jsx(CircularProgress, { size: 16 }) : undefined, autoFocus: true, children: isKeepingAlive ? "Refreshing..." : "Yes, I'm still here" }) })] }));
    return { keepaliveDialog };
}
//# sourceMappingURL=useSupersetKeepalive.js.map